import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import VoiceAssistant from "@/components/VoiceAssistant";
import Protected from "@/components/Protected";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import AgencyDashboard from "@/pages/AgencyDashboard";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Disclaimer from "@/pages/Disclaimer";
import CookiePolicy from "@/pages/CookiePolicy";
import FAQ from "@/pages/FAQ";
import CookieBanner from "@/components/CookieBanner";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Dashboard from "@/pages/Dashboard";
import PostJob from "@/pages/PostJob";
import ContractorJobDetail from "@/pages/ContractorJobDetail";
import AdminUsers from "@/pages/AdminUsers";
import About from "@/pages/About";
import AdminEnquiries from "@/pages/AdminEnquiries";
import Partners from "@/pages/Partners";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/AdminBlog";
import Plans from "@/pages/Plans";
import BillingHistory from "@/pages/BillingHistory";
import AdminRevenue from "@/pages/AdminRevenue";
import Pipeline from "@/pages/Pipeline";
import HRDashboard from "@/pages/HRDashboard";
import Consultancy from "@/pages/Consultancy";
import ConsultancyDetail from "@/pages/ConsultancyDetail";
import AdminDiscountCodes from "@/pages/AdminDiscountCodes";
import AdminConsultancy from "@/pages/AdminConsultancy";
import AdminEmailBroadcast from "@/pages/AdminEmailBroadcast";
import AdminExecutives from "@/pages/AdminExecutives";
import AdminCategories from "@/pages/AdminCategories";
import ExecutiveTeams from "@/pages/ExecutiveTeams";

const CONTRACTOR_ADMIN_ROLES = ["contractor", "hr", "admin"];
const HR_ROLES = ["contractor", "hr", "admin"];
const ADMIN_ROLES = ["admin"];

function AppRouter() {
  const location = useLocation();
  // OAuth callback: process #session_id=… synchronously during render (before ProtectedRoute).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/admin/blog" element={<Protected roles={ADMIN_ROLES}><AdminBlog /></Protected>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/agency/dashboard" element={<Protected roles={["agency", "admin"]}><AgencyDashboard /></Protected>} />
      <Route path="/jobs" element={<Jobs />} />
      <Route path="/jobs/:id" element={<JobDetail />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/post-job" element={<Protected roles={CONTRACTOR_ADMIN_ROLES}><PostJob /></Protected>} />
      <Route path="/contractor/jobs/:id" element={<Protected roles={CONTRACTOR_ADMIN_ROLES}><ContractorJobDetail /></Protected>} />
      <Route path="/admin/users" element={<Protected roles={ADMIN_ROLES}><AdminUsers /></Protected>} />
      <Route path="/admin/enquiries" element={<Protected roles={ADMIN_ROLES}><AdminEnquiries /></Protected>} />
      <Route path="/admin/revenue" element={<Protected roles={ADMIN_ROLES}><AdminRevenue /></Protected>} />
      <Route path="/admin/discount-codes" element={<Protected roles={ADMIN_ROLES}><AdminDiscountCodes /></Protected>} />
      <Route path="/admin/consultancy" element={<Protected roles={ADMIN_ROLES}><AdminConsultancy /></Protected>} />
      <Route path="/admin/email-broadcast" element={<Protected roles={ADMIN_ROLES}><AdminEmailBroadcast /></Protected>} />
      <Route path="/admin/executives" element={<Protected roles={ADMIN_ROLES}><AdminExecutives /></Protected>} />
      <Route path="/admin/categories" element={<Protected roles={ADMIN_ROLES}><AdminCategories /></Protected>} />
      <Route path="/executive-teams" element={<ExecutiveTeams />} />
      <Route path="/plans" element={<Plans />} />
      <Route path="/consultancy" element={<Consultancy />} />
      <Route path="/consultancy/:slug" element={<ConsultancyDetail />} />
      <Route path="/hr" element={<Protected roles={HR_ROLES}><HRDashboard /></Protected>} />
      <Route path="/hr/dashboard" element={<Protected roles={HR_ROLES}><HRDashboard /></Protected>} />
      <Route path="/billing" element={<Protected><BillingHistory /></Protected>} />
      <Route path="/contractor/pipeline" element={<Protected roles={["contractor","hr","admin"]}><Pipeline /></Protected>} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App min-h-screen">
            <AnalyticsTracker />
            <Header />
            <AppRouter />
            <VoiceAssistant />
            <SiteFooter />
            <CookieBanner />
            <Toaster position="top-right" richColors closeButton />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
